/**
 * oneThing — the single card on Today, derived from the Next-Step Engine.
 *
 * Today renders the engine's output (Doc 09; Doc 26 rule 7 — no parallel
 * engine). This module only selects and phrases: the first REQUIRED
 * responsibility by deadline is "the one thing"; if there is none, the ball
 * is named as being with others, or with nobody. It never reclassifies.
 */

import type { ResponsibilityCard } from '../../../types/prioritization/responsibility';
import { isResponsibilityValid } from '../../../types/prioritization/responsibility';

export interface OneThing {
  kind: 'do' | 'waiting' | 'clear';
  /** The headline sentence: what to do, or that nothing is needed. */
  headline: string;
  /** Who asked / why, in plain words. Empty when nothing is needed. */
  detail: string;
  /** "due Friday" style phrase, or null. */
  due: string | null;
  /** The engine's card, when kind === 'do'. */
  card: ResponsibilityCard | null;
  /** The button label, when kind === 'do'. */
  actionLabel: string | null;
  /** How many more things are needed after this one. */
  moreNeeded: number;
  /** For 'waiting': how many things the ball is with others on. */
  waitingCount: number;
}

const DAY_MS = 86_400_000;

export function duePhrase(deadline: string | null | undefined, today: Date = new Date()): string | null {
  if (!deadline) return null;
  const due = new Date(deadline);
  if (Number.isNaN(due.getTime())) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.floor((due.getTime() - start.getTime()) / DAY_MS);
  if (days < 0) return 'overdue';
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  if (days < 7) return `due ${due.toLocaleDateString('en-US', { weekday: 'long' })}`;
  return `due ${due.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
}

function byDeadline(a: ResponsibilityCard, b: ResponsibilityCard): number {
  if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
  if (a.deadline) return -1;
  if (b.deadline) return 1;
  return (a.created_at ?? '').localeCompare(b.created_at ?? '');
}

/** "Rank the options" → "Rank the options." — the engine's subject as a sentence. */
function asSentence(text: string): string {
  const t = text.trim();
  if (!t) return '';
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

export function selectOneThing(cards: ResponsibilityCard[], today: Date = new Date()): OneThing {
  const valid = cards.filter(isResponsibilityValid);
  const required = valid.filter((c) => c.classification === 'REQUIRED').sort(byDeadline);
  const waiting = valid.filter((c) => c.classification === 'WAITING');

  if (required.length > 0) {
    const card = required[0];
    return {
      kind: 'do',
      headline: asSentence(card.subject),
      detail: card.why?.trim() ?? '',
      due: duePhrase(card.deadline, today),
      card,
      actionLabel: card.valid_actions[0]?.label ?? 'Open',
      moreNeeded: required.length - 1,
      waitingCount: waiting.length,
    };
  }

  if (waiting.length > 0) {
    return {
      kind: 'waiting',
      headline: 'Nothing needs you right now.',
      detail:
        waiting.length === 1
          ? `The ball is with someone else on "${waiting[0].subject}". You will hear when it moves.`
          : `The ball is with other people on ${waiting.length} things. You will hear when any of them moves.`,
      due: null,
      card: null,
      actionLabel: null,
      moreNeeded: 0,
      waitingCount: waiting.length,
    };
  }

  return {
    kind: 'clear',
    headline: 'Nothing needs you right now.',
    detail: 'When someone asks for your input, it will appear here with one button.',
    due: null,
    card: null,
    actionLabel: null,
    moreNeeded: 0,
    waitingCount: 0,
  };
}

/** "Good morning" / "Good afternoon" / "Good evening" by the viewer's clock. */
export function greetingFor(date: Date = new Date()): string {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
